import { Injectable } from '@angular/core';
import { clone } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import Employee from 'src/app/model/product/employee';
import { GeocoderService } from 'src/app/services/geocoder.service';
import {
  EmployeeTourHelper,
  PreviewInfo,
} from '../employee-tour-list/employee-tour/helper/employee-tour-helper';
import { EmployeeDateTourControllerRequestService } from '../employee-tour-list/employee-tour/service/employee-date-tour-controller-request.service';
import { TourPreviewMarker } from '../immo-map/helper/immo-map-helper';
import { AffectedTour } from './assign-sub-project-to-tour-controller-request.service';
import { TourPlanningEventService } from './tour-planning-event.service';

@Injectable({
  providedIn: 'root',
})
export class TourPreviewStateService {
  private readonly _slot1: PreviewSlot = {
    id: 1,
    markerPath: '../../../../../../assets/tours/markers/blue-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#1e26e1',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };
  private readonly _slot2: PreviewSlot = {
    id: 2,
    markerPath: '../../../../../../assets/tours/markers/black-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#0b0e0e',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };
  private readonly _slot3: PreviewSlot = {
    id: 3,
    markerPath: '../../../../../../assets/tours/markers/red-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#d5452a',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };
  private readonly _slot4: PreviewSlot = {
    id: 4,
    markerPath: '../../../../../../assets/tours/markers/green-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#53c738',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };
  private readonly _slot5: PreviewSlot = {
    id: 5,
    markerPath: '../../../../../../assets/tours/markers/azure-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#4db2a7',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };
  private readonly _slot6: PreviewSlot = {
    id: 6,
    markerPath: '../../../../../../assets/tours/markers/purple-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#bb44ba',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };

  private readonly _slot7: PreviewSlot = {
    id: 7,
    markerPath: '../../../../../../assets/tours/markers/yellow-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#daa520',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };

  private readonly _slot8: PreviewSlot = {
    id: 8,
    markerPath: '../../../../../../assets/tours/markers/orange-preview-marker',
    directionsRenderer: new google.maps.DirectionsRenderer({
      preserveViewport: true,
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: '#ffa83d',
        strokeOpacity: 0.55,
        strokeWeight: 6,
      },
    }),
  };

  private readonly _allSlots: PreviewSlot[] = [
    this._slot1,
    this._slot2,
    this._slot3,
    this._slot4,
    this._slot5,
    this._slot6,
    this._slot7,
    this._slot8,
  ];

  private tourPreviewStateChangeSubject = new Subject<PreviewSlot[]>();

  private tourPreviewSlotOpen = new Subject<boolean>();

  private activePreviewSlots: PreviewSlot[] = [];

  constructor(
    private geocoderService: GeocoderService,
    private tourPlanningEventService: TourPlanningEventService,
    private employeeDateTourControllerRequestService: EmployeeDateTourControllerRequestService
  ) {
    this.initialize();
  }

  private initialize(): void {
    this.tourPlanningEventService
      .getTourChangedForPlacersSubject()
      .subscribe((affectedTours: AffectedTour[]) => {
        if (!CollectionHelper.isPopulated(affectedTours)) {
          return;
        }
        this._allSlots.forEach(async (s: PreviewSlot) => {
          if (s.state == null) {
            return;
          }
          const matchingTour = affectedTours.find(
            (t) =>
              s.state.placerId === t.placerId &&
              DateHelper.equal(s.state.date, t.date)
          );
          if (matchingTour != null) {
            const updatedTour =
              await this.employeeDateTourControllerRequestService.employeeDateTour(
                s.state.placerId,
                DateHelper.formatDate(s.state.date, 'yyyyMMdd')
              );
            if (
              updatedTour == null ||
              !CollectionHelper.isPopulated(updatedTour.tourItems)
            ) {
              s.state = null;
              s.directionsRenderer.setDirections({
                geocoded_waypoints: [],
                routes: [],
              });
              // Remove from active slots if it exists
              this.activePreviewSlots = this.activePreviewSlots.filter(
                (slot) => slot !== s
              );
            } else {
              const stateInput: TourPreviewStateInput =
                await EmployeeTourHelper.getTourPreviewStateInput(
                  updatedTour.placer,
                  updatedTour,
                  this.geocoderService
                );
              s.state = this.getStateForInput(stateInput);
            }
            this.emitFilledPreviewSlots();
            this.emitTourPreviewSlotOpen();
          }
        });
      });
  }

  setPreviewState(input: TourPreviewStateInput): void {
    const state = this.getStateForInput(input);

    const slotToFill = this.getSlotToFill(input.placer.id, input.date);
    slotToFill.state = state;

    // Add to active slots or move to end if already there (most recent)
    const existingIndex = this.activePreviewSlots.indexOf(slotToFill);
    if (existingIndex >= 0) {
      this.activePreviewSlots.splice(existingIndex, 1);
    }
    this.activePreviewSlots.push(slotToFill);

    this.emitFilledPreviewSlots();
    this.emitTourPreviewSlotOpen();
  }

  private getStateForInput(input: TourPreviewStateInput): TourPreviewState {
    return {
      placerId: input.placer.id,
      placerFirstName:
        input.placer.user == null || input.placer.user.firstName == null
          ? ''
          : input.placer.user.firstName.toString(),
      date: input.date,
      placerAddressCoordinatesIncluded: input.placerAddressCoordinatesIncluded,
      previewInfo: input.previewInfo,
    };
  }

  private getSlotToFill(placerId: number, date: Date): PreviewSlot {
    let toReturn: PreviewSlot = null;
    for (const slot of this._allSlots) {
      if (
        (slot.state != null &&
          slot.state.placerId === placerId &&
          DateHelper.equal(slot.state.date, date)) ||
        (toReturn == null && slot.state == null)
      ) {
        toReturn = slot;
      }
    }
    return toReturn == null
      ? this._allSlots[this._allSlots.length - 1]
      : toReturn;
  }

  previewActiveFor(placerId: number, date: Date): boolean {
    for (const slot of this._allSlots) {
      if (
        slot.state != null &&
        slot.state.placerId === placerId &&
        DateHelper.equal(slot.state.date, date)
      ) {
        return true;
      }
    }
    return false;
  }

  setMultipleTours(items: PreviewMultipleToursItem[]): void {
    if (!CollectionHelper.isPopulated(items)) {
      return;
    }

    // Clear existing active slots
    this.activePreviewSlots = [];

    let commonCursor = 0;

    for (
      commonCursor;
      commonCursor < this._allSlots.length && commonCursor < items.length;
      commonCursor++
    ) {
      const item = items[commonCursor];
      const slot = this._allSlots[commonCursor];

      const state: TourPreviewState = {
        placerId: item.placer.id,
        placerFirstName:
          item.placer.user == null || item.placer.user.firstName == null
            ? ''
            : item.placer.user.firstName.toString(),
        date: item.date,
        placerAddressCoordinatesIncluded: item.placerAddressCoordinatesIncluded,
        previewInfo: item.previewInfo,
      };
      slot.state = state;

      // Add to active slots
      this.activePreviewSlots.push(slot);
    }

    for (commonCursor; commonCursor < this._allSlots.length; commonCursor++) {
      const slot = this._allSlots[commonCursor];
      slot.state = null;
      slot.directionsRenderer.setDirections({
        geocoded_waypoints: [],
        routes: [],
      });
    }

    this.emitFilledPreviewSlots();
    this.emitTourPreviewSlotOpen();
  }

  resetPreviewStates(): void {
    this._allSlots.forEach((s) => {
      s.state = null;
      s.directionsRenderer.setDirections({
        geocoded_waypoints: [],
        routes: [],
      });
    });
    this.activePreviewSlots = [];
    this.emitFilledPreviewSlots();
    this.emitTourPreviewSlotOpen();
  }

  unsetPreviewState(placerId: number, date: Date): void {
    const matchingSlot = this._allSlots.find(
      (s) =>
        s.state != null &&
        s.state.placerId === placerId &&
        DateHelper.equal(s.state.date, date)
    );
    if (matchingSlot != null) {
      matchingSlot.state = null;
      matchingSlot.directionsRenderer.setDirections({
        geocoded_waypoints: [],
        routes: [],
      });
      // Remove from active slots
      this.activePreviewSlots = this.activePreviewSlots.filter(
        (slot) => slot !== matchingSlot
      );
    }
    this.emitFilledPreviewSlots();
    this.emitTourPreviewSlotOpen();
  }

  private emitFilledPreviewSlots(): void {
    const filledSlots: PreviewSlot[] = this._allSlots
      .filter((s) => s.state != null)
      .map((s) => clone(s));
    this.tourPreviewStateChangeSubject.next(filledSlots);
  }

  private emitTourPreviewSlotOpen(): void {
    this.tourPreviewSlotOpen.next(this._allSlots.some((s) => s.state == null));
  }

  setDirectionRendererMaps(map: google.maps.Map): void {
    this._allSlots.forEach((s) => s.directionsRenderer.setMap(map));
  }

  resetDirectionRenderers(): void {
    this._allSlots.forEach((s) => s.directionsRenderer.setMap(null));
  }

  getTourPreviewStateChangeObservable(): Observable<PreviewSlot[]> {
    return this.tourPreviewStateChangeSubject;
  }

  getTourPreviewSlotOpenObservable(): Observable<boolean> {
    return this.tourPreviewSlotOpen;
  }

  getLastActivatedPreviewInstaller(): Employee | null {
    if (this.activePreviewSlots.length === 0) {
      return null;
    }

    // Return the most recently activated preview (last in the list)
    const mostRecentSlot =
      this.activePreviewSlots[this.activePreviewSlots.length - 1];
    return mostRecentSlot?.state
      ? ({ id: mostRecentSlot.state.placerId } as Employee)
      : null;
  }

  getActivePreviewSlots(): PreviewSlot[] {
    return [...this.activePreviewSlots];
  }
}

export interface TourPreviewStateInput {
  placer: Employee;
  date: Date;
  placerAddressCoordinatesIncluded: boolean;
  previewInfo: PreviewInfo[];
}

export interface TourPreviewState {
  placerId: number;
  placerFirstName: string;
  date: Date;
  placerAddressCoordinatesIncluded: boolean;
  previewInfo: PreviewInfo[];
}

export interface PreviewSlot {
  id: number;
  state?: TourPreviewState;
  markers?: TourPreviewMarker[];
  markerPath: string;
  directionsRenderer: google.maps.DirectionsRenderer;
  totalDistance?: string;
  routeBounds?: google.maps.LatLngBounds;
}

export interface PreviewMultipleToursItem {
  placer: Employee;
  date: Date;
  placerAddressCoordinatesIncluded: boolean;
  previewInfo: PreviewInfo[];
}
